import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import { compose, defaultTo, find, isEmpty, path, propEq, reduce } from 'ramda';
import { withFormik } from 'formik';
import moment from 'moment';

import { apiRequestMembership } from '../../../api/membership';
import { handleErrorBase, handleSuccessBase } from '../../../util/forms';
import { routes } from '../../../routes';
import userActions from '../../../redux/user';
import { isNilOrEmpty } from '../../../util/redux';
import magic from '../../../util/magic';
import { getFileWithKey, toCurrency } from '../../../util/helpers';
import { apiGetMembershipRenewalDiscount } from '../../../api/discount';

import FancyRadio from '../../../components/Forms/FancyRadio';
import FileBrowser from '../../../components/Forms/FileBrowser';
import Checkbox from '../../../components/Forms/Checkbox';
import Card from '../../../components/Card';
import DiscountForm from '../../../components/DiscountForm';
import StripeCheckout from '../../../components/StripeCheckout';
import EditField from '../../../components/Forms/EditField';

const mapStore = ({ certifications, user, memberships }) => ({
  certifications,
  user,
  memberships,
});

const mapDispatch = {
  ...userActions,
};

class Renew extends Component {
  static propTypes = {
    certifications: PropTypes.array,
    history: PropTypes.object,
    user: PropTypes.object,
    memberships: PropTypes.array,
    updateUserMembership: PropTypes.func,
    updateUserLatestMembership: PropTypes.func,
    updateUser: PropTypes.func,
  };

  render() {
    const {
      memberships,
      errors,
      values,
      handleBlur,
      handleChange,
      history,
      isValid,
      setValues,
      setFieldValue,
      user,
      updateUserLatestMembership,
      updateUserMembership,
      updateUser,
    } = this.props;
    const isMember = path(['membership', 'valid'], user);
    const proMembership = memberships.find((option) => option.name.toLowerCase().indexOf('professional') !== -1);
    const certMembership = memberships.find((option) => option.name.toLowerCase().indexOf('certification') !== -1);
    const studentMembership = memberships.find((option) => option.name.toLowerCase().indexOf('student') !== -1);
    const emeritusMembership = memberships.find((option) => option.name.toLowerCase().indexOf('emeritus') !== -1);
    const selectedMembership = defaultTo({}, memberships.find(propEq('categoryId', Number(values.membershipId))));

    const registrations = user.latestRegistrations.filter(
      (r) =>
        r.status != magic.complete &&
        r.status != magic.preRegistration &&
        r.status != magic.registrationApproval &&
        (!r.deadline || moment().diff(moment(r.deadline), 'months') < 0) &&
        r.trainingId == null,
    );

    return (
      <Card className="container">
        <Card.Header>{isMember ? 'Renew your membership' : 'Choose a membership'}</Card.Header>
        <Card.Body>
          <p>
            Sage Financial Solutions Membership provides you with a diverse and inclusive professional network, free professional
            development opportunities, and resources and benefits that positively impact your work and enhance your
            career.
          </p>
          <p>
            <strong>NOTE:</strong> Sage Financial Solutions® membership and certification are completely separate divisions of the
            organization. While membership offers additional benefits to our certified professionals and provides a
            community for them to network and share best practices, membership is not required to hold certification.
            Sage Financial Solutions® Membership dues are nonrefundable.{' '}
            <a href={routes.wordPress.refundPolicy} target="_blank">
              Read Sage Financial Solutions®’s Refund Policy here
            </a>
            .
          </p>
          <p className="dark-blue-heading font-weight-bold">
            * Membership runs on a calendar year (Jan 1 – Dec 31).{' '}
            <a href={routes.wordPress.membership} target="_blank">
              Learn more &gt;
            </a>
          </p>

          {user.symposiumRegistration ? (
            <p className="font-weight-bold">
              Because you are currently registered for this year's Symposium, you will automatically receive next year's
              membership once Symposium ends.
            </p>
          ) : (
            <Fragment>
              {certMembership && registrations.length > 0 && (
                <FancyRadio
                  name="membershipId"
                  label={`${certMembership.name} (${toCurrency(values.prices[certMembership.id])})`}
                  description={certMembership.description}
                  value={certMembership.categoryId}
                  checked={values.membershipId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              )}

              {proMembership && registrations.length == 0 && (
                <FancyRadio
                  name="membershipId"
                  label={`${proMembership.name} (${toCurrency(values.prices[proMembership.id])})`}
                  description={proMembership.description}
                  value={proMembership.categoryId}
                  checked={values.membershipId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              )}

              {studentMembership && (
                <FancyRadio
                  name="membershipId"
                  label={`${studentMembership.name} (${toCurrency(values.prices[studentMembership.id])})`}
                  description={studentMembership.description}
                  value={studentMembership.categoryId}
                  checked={values.membershipId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                >
                  {values.membershipId == studentMembership.categoryId && (
                    <div>
                      <FileBrowser
                        error={errors.studentMembershipFile}
                        files={values.media}
                        fileKey={magic.studentMembership}
                        label="Please submit your transcripts"
                        name="studentMembershipFile"
                        fake
                        fakeFiles={values.fakeMedia}
                        fakeUpdate={({ files, fakeFiles }) =>
                          setValues({ ...values, media: files, fakeMedia: fakeFiles })
                        }
                      />
                    </div>
                  )}
                </FancyRadio>
              )}

              {emeritusMembership && (
                <FancyRadio
                  name="membershipId"
                  label={`${emeritusMembership.name} (${toCurrency(values.prices[emeritusMembership.id])})`}
                  description={emeritusMembership.description}
                  value={emeritusMembership.categoryId}
                  checked={values.membershipId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                >
                  {values.membershipId == emeritusMembership.categoryId && (
                    <div>
                      <Checkbox
                        name="memberForTenYears"
                        checked={values.memberForTenYears}
                        error={errors.memberForTenYears}
                        label="I've been a member for 10 consecutive years"
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                      <EditField
                        block
                        name="dateOfBirth"
                        label="Confirm your date of birth"
                        type="date"
                        error={errors.dateOfBirth}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.dateOfBirth}
                      />
                    </div>
                  )}
                </FancyRadio>
              )}

              <DiscountForm
                discount={values.discount}
                checkDiscount={(discountCode) => apiGetMembershipRenewalDiscount({ discountCode })}
                update={(data) => {
                  setFieldValue(
                    'prices',
                    reduce((prices, price) => ({ ...prices, [price.id]: price.price / 100 }), {}, data.payments),
                    false,
                  );
                  setFieldValue('discount', defaultTo({}, data.discount), false);
                }}
              />

              {values.membershipId && (
                <p>
                  * Membership is not the same as annual certification fees. To pay your annual certification fee,
                  please click <Link to={routes.certificationsAndTraining.certifications.pay}>here</Link>.
                </p>
              )}

              <div className="text-right">
                <StripeCheckout
                  name="Sage Financial Solutions"
                  description="Membership Payment"
                  amount={defaultTo(0, values.prices[selectedMembership.id])}
                  disabled={!isValid}
                  pay={(token, overrides = {}) =>
                    apiRequestMembership({
                      ...values,
                      discountCode: path(['discount', 'code'], values),
                      token,
                      ...overrides,
                    })
                      .then(({ data }) => {
                        updateUserMembership(data.membership);
                        updateUserLatestMembership(data.latestMembershipRequest);
                      })
                      // TODO: Sage Financial Solutions might want this removed after people are finished purchasing their 2020 membership
                      .then(() => updateUser({ physicalJournal: false }))
                      .then(() => history.push(routes.membership.paid))
                      .then(() => handleSuccessBase())
                      .catch(() => handleErrorBase())
                  }
                />
              </div>
            </Fragment>
          )}
        </Card.Body>
      </Card>
    );
  }
}

export default compose(
  withRouter,
  connect(mapStore, mapDispatch),
  withFormik({
    handleSubmit: (payload, formProps) => {},
    mapPropsToValues: ({ user, memberships }) => {
      const prices = reduce(
        (prices, membership) => ({ ...prices, [membership.id]: membership.price }),
        {},
        memberships,
      );

      return {
        membershipId: '',
        studentMembershipFile: '',
        media: [],
        fakeMedia: [],
        memberForTenYears: false,
        dateOfBirth: defaultTo(new Date().toISOString().substr(0, 10), user.dateOfBirth),
        prices,
        discount: {},
      };
    },
    validate: (values, props) => {
      const { memberships } = props;
      const errors = {};
      const selectedMembershipName = compose(
        defaultTo(''),
        path(['name']),
        find(propEq('categoryId', Number(values.membershipId))),
      )(memberships);
      const isEmeritus = selectedMembershipName.toLowerCase().indexOf('emeritus') !== -1;
      const isStudent = selectedMembershipName.toLowerCase().indexOf('student') !== -1;

      if (isNilOrEmpty(values.membershipId)) {
        errors.membershipId = 'You must pick a membership option';
      } else if (isEmeritus) {
        if (moment().diff(values.dateOfBirth, 'years') < 65) {
          errors.dateOfBirth = 'You must be 65 or older to qualify for this membership';
        }

        if (!values.memberForTenYears) {
          errors.memberForTenYears = 'You must be a member for 10 consecutive years to qualify for this membership';
        }
      } else if (isStudent && !getFileWithKey(values.fakeMedia, magic.studentMembership)) {
        errors.studentMembershipFile = 'Required';
      }

      return errors;
    },
    isInitialValid: ({ initialValues, validate }) => isEmpty(validate(initialValues)),
    validateOnChange: true,
  }),
)(Renew);
